// Wave AW.3-React Wave 4 (2026-05-16) — 외부 바이어 즐겨찾기 화면
// API: GET /buyer-app/me/favorites → { success, data: { auctions: [...] } }
// 백엔드 status='active' 필터 적용됨 — 클라이언트 추가 필터 불필요.
//
// 토글 해제 (favorited === false) 시 목록에서 즉시 제거 — 카드의 onFavoriteChange callback.
// 서버 응답 후 제거 (~300ms lag) — optimistic 제거는 실패 시 rollback UX 복잡.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../../utils/api';
import { authBuyerFetch, isUnauthorized, handleUnauthorized, displayApiError } from './buyerApi';
import AuctionBuyerCard from './AuctionBuyerCard';
import AuctionBuyerLayout from './AuctionBuyerLayout';
import EmptyState from './EmptyState';
import { useT } from '../../i18n/I18nProvider';

function AuctionBuyerFavoritesList() {
  var navigate = useNavigate();
  var t = useT();
  var [items, setItems] = useState([]);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState('');

  var load = useCallback(function() {
    setLoading(true);
    setError('');
    authBuyerFetch(API + '/buyer-app/me/favorites', {})
      .then(function(r) {
        if (isUnauthorized(r)) { handleUnauthorized(navigate); throw new Error('unauthorized'); }
        return r.json();
      })
      .then(function(d) {
        if (d && d.success && d.data) {
          setItems(d.data.auctions || []);
        } else {
          setError(displayApiError(d, t, 'errListLoad'));
        }
        setLoading(false);
      })
      .catch(function(e) {
        if (e && e.message === 'unauthorized') return;
        setError(t('commonNetwork'));
        setLoading(false);
      });
  }, [navigate, t]);

  useEffect(function() { load(); }, [load]);

  // 카드 즐겨찾기 토글 콜백 — 명시적 false 체크 (advisor 정합) + functional setState (stale closure 차단).
  var handleFavoriteChange = useCallback(function(auctionId, favorited) {
    if (favorited === false) {
      setItems(function(prev) {
        return prev.filter(function(a) { return a.id !== auctionId; });
      });
    }
    // favorited === true 인 케이스는 이 화면에서 발생 안 함 (모든 카드가 favorited 상태로 진입).
  }, []);

  return (
    <AuctionBuyerLayout activeKey="favorites">
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '32px 32px 80px' }}>
        {/* 헤더 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>
            {t('favoritesTitle')}
            {!loading && !error && (
              <span style={{ marginLeft: 10, fontSize: 14, fontWeight: 600, color: '#64748b' }}>
                ({items.length})
              </span>
            )}
          </h2>
        </div>

        {/* 본문 */}
        {loading && (
          <div style={{ padding: '60px 0', textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
            {t('commonLoading')}
          </div>
        )}

        {!loading && error && (
          <div style={{ padding: '60px 0', textAlign: 'center' }}>
            <div style={{ color: '#dc2626', fontSize: 14, marginBottom: 12 }}>{error}</div>
            <button type="button" onClick={load}
              style={{
                padding: '8px 16px',
                background: 'white', border: '1.5px solid #e2e8f0', borderRadius: 8,
                fontSize: 13, fontWeight: 600, color: '#475569', cursor: 'pointer',              }}>
              {t('commonRetry')}
            </button>
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <EmptyState icon="⭐" title={t('favoritesEmpty')} hint={t('favoritesEmptyHint')}
            action={{ label: t('favoritesEmptyCta'), onClick: function(){ navigate('/buyer/auctions'); } }} />
        )}

        {!loading && !error && items.length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: 16,
          }}>
            {items.map(function(a) {
              return (
                <AuctionBuyerCard
                  key={a.id}
                  auction={a}
                  onClick={function(){ navigate('/buyer/auctions/' + a.id); }}
                  onFavoriteChange={handleFavoriteChange}
                />
              );
            })}
          </div>
        )}
      </div>
    </AuctionBuyerLayout>
  );
}

export default AuctionBuyerFavoritesList;
